import { ActivityType, TaskStatus } from "@repo/api/client";
import { isErrorPayload } from "./activityLog/errorPayload";
import type { ActivityLogItem, Task } from "./db/schema";

/**
 * Why an agent-run task is waiting on a person, or undefined when it is not.
 *
 * The agent does nothing on a task whose newest activity is an error
 * (`processAgentNextStep` returns before calling the model), and most error
 * paths leave the task agent-owned. Every system activity sets Action
 * Required, so in the list such a task looked identical to one the agent was
 * still working. A comment moves the newest activity past the error and wakes
 * the agent, which is how staff restart it, so the pause ends there too.
 *
 * `support` is an error the clinic cannot fix (`isUserActionable` false), which
 * says to contact Skyway support.
 */
export type AgentPause = "clinic" | "support";

export function agentPauseFor(
	task: Pick<Task, "assigneeType" | "status" | "runningJobs"> | undefined,
	newest: Pick<ActivityLogItem, "activityType" | "payload"> | undefined,
): AgentPause | undefined {
	if (task?.assigneeType !== "agent") return undefined;
	// Finished work is not waiting on anyone, whatever its last item says.
	if (task.status === TaskStatus.Completed) return undefined;
	// A running job means the agent is mid-turn; its next item is on the way.
	if (task.runningJobs.length > 0) return undefined;
	// `activityType` is marked deprecated in favour of `payload.type`, but it is
	// the field `processAgentNextStep` gates on, and this has to match that gate.
	if (newest?.activityType !== ActivityType.Error) return undefined;
	if (!isErrorPayload(newest.payload)) return "support";
	// A rerun is already scheduled, so nobody is being waited on.
	if (newest.payload.autoRetry) return undefined;

	return newest.payload.isUserActionable === true ? "clinic" : "support";
}
